/**
 * useAuth (CareStart3 port) — experimental shared-hooks useAuth adapted to this repo.
 *
 * WHAT: Re-implements the experimental `useAuth` tier API (subscriptionTier as
 *       free/small/medium/large, hasTierAccess, isTier*, detectedCountry) so the
 *       ported CareStart3 / symptom-checker components get the shape they expect.
 * WHY: This repo's own `hooks/useAuth` exposes a different (admin/org-centric) shape.
 *      Rather than fork every consumer, this reads THIS repo's real `AuthContext`
 *      (same provider that wraps the app) and derives the tier fields locally.
 * INVARIANT: Wired to the live AuthProvider via `context/Auth`; no second provider.
 */

import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { AuthContext } from "context/Auth";
import { tierAtLeast, isDevAuthFirestoreUser } from "@healthdesk/shared-utils";

/** Firestore Timestamp, ISO string, epoch, or Date -> Date (or null). */
const toDate = (timestamp) => {
  if (timestamp == null) return null;
  if (typeof timestamp === "object" && typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dbError, setDbError] = useState(null);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const {
    user,
    subscription,
    subscriptionData,
    userData,
    userLoading,
    logout,
    invoices,
    medications,
    reminderStatuses,
    medicationsLoading,
    addMedication,
    updateMedication,
    deleteMedication,
    refreshReminderStatuses,
    loadMedications,
    trackingData,
    loadTrackingData,
    clearTrackingData,
    forceResetTrackingData,
  } = context;

  const isAuthenticated = useMemo(
    () => user !== null && !userLoading,
    [user, userLoading],
  );

  const isVerified = useMemo(
    () => isAuthenticated && user?.emailVerified,
    [isAuthenticated, user],
  );

  const hasRole = useMemo(
    () => isVerified && userData?.role,
    [isVerified, userData],
  );

  const hasActiveTrial = useMemo(() => {
    if (!userData?.trialExpiresAt) return false;
    return toDate(userData.trialExpiresAt) > new Date();
  }, [userData?.trialExpiresAt]);

  const hasActiveSubscription = useMemo(
    () => subscriptionData && subscriptionData.status === "active",
    [subscriptionData],
  );

  const hasActiveDailyPass = useMemo(() => {
    const end = toDate(userData?.dailyPassExpiresAt);
    return end != null && end > new Date();
  }, [userData?.dailyPassExpiresAt]);

  const hasValidSubscription = useMemo(
    () =>
      hasRole && (hasActiveTrial || hasActiveSubscription || hasActiveDailyPass),
    [hasRole, hasActiveTrial, hasActiveSubscription, hasActiveDailyPass],
  );

  const isPlus = hasActiveSubscription;

  const subscriptionTier = useMemo(() => {
    const isDevByAuthEmail =
      typeof user?.email === "string" &&
      user.email.toLowerCase().endsWith("@dev.local");
    if (isDevAuthFirestoreUser(userData) || isDevByAuthEmail) {
      const t = userData?.subscriptionTier;
      if (t === "free" || t === "small" || t === "medium" || t === "large") {
        return t;
      }
      return "large";
    }
    if (hasActiveSubscription) {
      return userData?.subscriptionTier || "medium";
    }
    if (hasActiveTrial || hasActiveDailyPass) return "medium";
    return "free";
  }, [
    user?.email,
    userData,
    hasActiveSubscription,
    hasActiveTrial,
    hasActiveDailyPass,
  ]);

  const isTierFree = subscriptionTier === "free";
  const isTierSmall = subscriptionTier === "small";
  const isTierMedium = subscriptionTier === "medium";
  const isTierLarge = subscriptionTier === "large";

  const hasTierAccess = useCallback(
    (minTier) => tierAtLeast(subscriptionTier, minTier),
    [subscriptionTier],
  );

  const hasLapsedSubscription = useMemo(
    () =>
      subscriptionData &&
      ["canceled", "past_due", "unpaid"].includes(subscriptionData.status),
    [subscriptionData],
  );

  const canStartTrial = useMemo(
    () =>
      hasRole &&
      ["patient", "professional", "facility"].includes(userData?.role) &&
      !userData?.hasUsedTrial &&
      !hasActiveSubscription,
    [hasRole, userData?.role, userData?.hasUsedTrial, hasActiveSubscription],
  );

  const trialExpired = useMemo(
    () =>
      userData?.hasUsedTrial &&
      !hasActiveTrial &&
      !subscriptionData &&
      !userData?.trialConsumedByUpgrade,
    [
      userData?.hasUsedTrial,
      hasActiveTrial,
      subscriptionData,
      userData?.trialConsumedByUpgrade,
    ],
  );

  const activeSubscriptionRole = useMemo(() => {
    if (!isVerified) return undefined;
    if (hasActiveTrial || hasActiveDailyPass || hasActiveSubscription) {
      return userData?.role;
    }
    return undefined;
  }, [
    isVerified,
    hasActiveTrial,
    hasActiveDailyPass,
    hasActiveSubscription,
    userData?.role,
  ]);

  const canUseFreeTrial = useMemo(
    () => subscription === "facility" && !userData?.hasUsedFreeTrial,
    [subscription, userData],
  );

  const resendEmail = async () => {
    setLoading(true);
    setDbError(null);
    try {
      await user.sendEmailVerification();
      setSubmitted(true);
    } catch (error) {
      setDbError(error.message);
      console.error("Email verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setDbError(null);
      setSubmitted(false);
    }
  }, [user]);

  const pendingInvoice = useMemo(
    () => invoices?.find((inv) => inv.status === "open"),
    [invoices],
  );

  return {
    user,
    userData,
    subscription,
    subscriptionData,
    subscriptionTier,
    userLoading,
    loading,
    submitted,
    dbError,
    isAuthenticated,
    isVerified,
    hasRole,
    hasValidSubscription,
    hasActiveDailyPass,
    hasActiveTrial,
    hasActiveSubscription,
    isPlus,
    isTierFree,
    isTierSmall,
    isTierMedium,
    isTierLarge,
    hasTierAccess,
    hasLapsedSubscription,
    canStartTrial,
    trialExpired,
    activeSubscriptionRole,
    canUseFreeTrial,
    detectedCountry: userData?.detectedCountry || null,
    resendEmail,
    logout,
    invoices,
    pendingInvoice,
    medications,
    reminderStatuses,
    medicationsLoading,
    addMedication,
    updateMedication,
    deleteMedication,
    refreshReminderStatuses,
    loadMedications,
    trackingData,
    loadTrackingData,
    clearTrackingData,
    forceResetTrackingData,
  };
};

export default useAuth;
